import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { clientesService } from '@/api/clientes';
import { articulosService } from '@/api/articulos';
import { salesService } from '@/api/sales';
import AppHeader from '@/components/layout/AppHeader';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import Button from '@/components/common/Button';
import { useCartStore } from '@/store/cartStore';
import { useEmpresaStore } from '@/store/empresaStore';
import { Package } from 'lucide-react';
import styles from '@/styles/ui.module.css';

const statusBadgeClasses = {
  green: styles.orderBadgeGreen,
  blue: styles.orderBadgeBlue,
  yellow: styles.orderBadgeYellow,
  red: styles.orderBadgeRed,
};

const statusMap: Record<number, { label: string; style: keyof typeof statusBadgeClasses }> = {
  0: { label: 'Pendiente', style: 'yellow' },
  2: { label: 'Servido', style: 'green' },
};

export default function ClienteDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { setClienteId, addMultipleItems, clearCart } = useCartStore();
  const empresaSeleccionada = useEmpresaStore((state) => state.empresaSeleccionada);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  const {
    data: cliente,
    isLoading: loadingCliente,
  } = useQuery({
    queryKey: ['cliente', id],
    queryFn: () => clientesService.getClienteById(id!),
    enabled: !!id,
  });

  const {
    data: pedidosData,
    isLoading: loadingPedidos,
  } = useQuery({
    queryKey: ['cliente-pedidos', id],
    queryFn: () => clientesService.getPedidos(id!, { limit: 10 }),
    enabled: !!id,
  });

  const { data: salesSummary } = useQuery({
    queryKey: ['cliente-sales-summary', id],
    queryFn: () => salesService.getCustomerSummary({ customer: Number(id), empresa: empresaSeleccionada?.id }),
    enabled: !!id && !!empresaSeleccionada,
  });

  const { data: productsData } = useQuery({
    queryKey: ['cliente-products', id],
    queryFn: () => salesService.getCustomerProducts({ customer: Number(id), empresa: empresaSeleccionada?.id }),
    enabled: !!id && !!empresaSeleccionada,
  });

  const salesChartData = useMemo(() => {
    if (salesSummary && (salesSummary as any).years && Array.isArray((salesSummary as any).years)) {
      return (salesSummary as any).years.map((yearData: any) => ({
        year: yearData.year.toString(),
        ventas: yearData.ventas || 0,
      }));
    }

    return [];
  }, [salesSummary]);

  const salesKpis = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const targetYears = [currentYear - 2, currentYear - 1, currentYear];
    const salesByYear = new Map<number, number>(
      salesChartData.map((item: any) => [Number(item.year), Number(item.ventas || 0)])
    );

    return targetYears.map((year, index, array) => {
      const ventas = salesByYear.get(year) ?? 0;
      const previousYear = index > 0 ? array[index - 1] : null;
      const previousVentas = previousYear ? salesByYear.get(previousYear) ?? 0 : 0;
      const changePercent =
        previousYear !== null && previousVentas > 0
          ? ((ventas - previousVentas) / previousVentas) * 100
          : null;

      return {
        year: String(year),
        ventas,
        previousYear: previousYear ? String(previousYear) : null,
        changePercent,
      };
    });
  }, [salesChartData]);

  const productsWithTrends = useMemo(() => {
    if (!productsData || !(productsData as any).items || !Array.isArray((productsData as any).items)) {
      return [];
    }

    return (productsData as any).items
      .map((product: any, index: number) => ({
        productId: product.articuloId || `unknown-${index}`,
        productCode: product.codigoArticulo || '',
        productName: product.descripcionArticulo || '',
        totalUnits: product.unidades || 0,
        totalSales: product.ventas || 0,
        totalOrders: product.pedidos || 0,
      }))
      .filter((p: any) => p.totalUnits > 0)
      .sort((a: any, b: any) => b.totalUnits - a.totalUnits);
  }, [productsData]);

  const handleCrearPedido = async () => {
    if (!id) return;
    setLoadingSuggestions(true);
    try {
      clearCart();
      setClienteId(id);

      const historial = await articulosService.getHistorialCliente(Number(id), 2, empresaSeleccionada?.id);
      if (historial?.length) {
        const articulosPromises = historial.slice(0, 8).map((item) =>
          articulosService.getArticuloById(item.articuloId)
        );
        const articulos = await Promise.all(articulosPromises);

        const itemsToAdd = articulos.map((articulo) => ({
          articulo,
          cantidad: 1,
          descuento: articulo.descuentoMax > 0 ? Math.min(5, articulo.descuentoMax) : 0,
        }));

        addMultipleItems(itemsToAdd);
        navigate('/catalogo', {
          state: { message: `Se han agregado ${articulos.length} articulos frecuentes al carrito` },
        });
      } else {
        navigate('/catalogo');
      }
    } catch (error) {
      console.error('Error al cargar articulos sugeridos:', error);
      setClienteId(id);
      navigate('/catalogo');
    } finally {
      setLoadingSuggestions(false);
    }
  };

  if (loadingCliente) {
    return (
      <div className={styles.page}>
        <AppHeader title="Detalle del Cliente" subtitle="Revisa historial, saldos y crea propuestas" />
        <div className={styles.screenWrapper}>
          <section className={styles.section}>
            <LoadingSpinner />
          </section>
        </div>
      </div>
    );
  }

  if (!cliente) {
    return (
      <div className={styles.page}>
        <AppHeader title="Detalle del Cliente" subtitle="Revisa historial, saldos y crea propuestas" />
        <div className={styles.screenWrapper}>
          <section className={styles.section}>
            <div className={styles.emptyState}>
              <span className="material-symbols-outlined" style={{ fontSize: '48px', color: '#94a3b8' }}>
                error
              </span>
              <h3 className={styles.emptyTitle}>Cliente no encontrado</h3>
              <p className={styles.mutedText}>Verifica la seleccion o regresa a la lista de clientes.</p>
            </div>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <AppHeader title="Detalle del Cliente" subtitle="Revisa historial, saldos y crea propuestas" />
      <div className={styles.screenWrapper}>
        <section className={styles.section}>
          <div className={styles.card}>
            <div className={styles.cardRowBetween}>
              <div className={styles.columnGap}>
                <p className={styles.sectionTitle}>{cliente.nombre}</p>
                <div className={styles.infoRow}>
                  <span className="material-symbols-outlined">store</span>
                  <span className={styles.mutedText}>{cliente.codigo}</span>
                </div>
                <p className={styles.mutedText}>
                  {cliente.direccion}, {cliente.ciudad}
                </p>
              </div>
              <Button onClick={handleCrearPedido} className={styles.detailActionButton} disabled={loadingSuggestions}>
                {loadingSuggestions ? (
                  <>
                    <span className="material-symbols-outlined">hourglass_top</span>
                    Preparando pedido...
                  </>
                ) : (
                  <>
                    <span className="material-symbols-outlined">add_shopping_cart</span>
                    Crear Nuevo Pedido
                  </>
                )}
              </Button>
            </div>
          </div>
        </section>

        <section className="mt-6 mb-6 px-4">
          <h3 className="mb-3 text-lg font-bold text-gray-900 dark:text-white">
            Ventas de los Ultimos 3 Anos
          </h3>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            {salesKpis.length > 0 ? (
              salesKpis.map((item: any) => {
                const isPositive = item.changePercent !== null && item.changePercent >= 0;
                const badgeClass =
                  item.changePercent === null
                    ? 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-300'
                    : isPositive
                      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                      : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';
                const detailClass =
                  item.changePercent === null
                    ? 'text-slate-500 dark:text-slate-300'
                    : isPositive
                      ? 'text-emerald-600 dark:text-emerald-300'
                      : 'text-red-600 dark:text-red-300';

                return (
                  <div
                    key={item.year}
                    className="rounded-xl border border-slate-200 bg-white p-5 shadow-md dark:border-slate-700 dark:bg-gray-800"
                  >
                    <div className="mb-4 flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">
                          {item.year}
                        </p>
                        <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
                          {item.ventas.toFixed(2)}€
                        </p>
                      </div>
                      <span className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${badgeClass}`}>
                        {item.changePercent === null ? 'N/A' : isPositive ? 'Incremento' : 'Descenso'}
                      </span>
                    </div>
                    <p className={`text-sm font-semibold ${detailClass}`}>
                      {item.changePercent === null
                        ? 'Sin base comparativa'
                        : `${isPositive ? '+' : ''}${item.changePercent.toFixed(1)}% vs ${item.previousYear}`}
                    </p>
                    <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                      Comparativa anual sobre ventas acumuladas del cliente.
                    </p>
                  </div>
                );
              })
            ) : (
              <p className="rounded-xl bg-white py-8 text-center text-gray-500 shadow-md dark:bg-gray-800 dark:text-gray-400 lg:col-span-3">
                No hay datos de ventas disponibles
              </p>
            )}
          </div>
        </section>

        <section className="px-4 mb-4">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
            <Package className="w-5 h-5" />
            Productos Mas Comprados
          </h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {productsWithTrends.length > 0 ? (
              productsWithTrends.slice(0, 6).map((product: any) => {
                return (
                  <div
                    key={product.productId}
                    className="rounded-xl border border-slate-200 bg-white p-4 shadow-md transition-colors hover:bg-gray-50 dark:border-slate-700 dark:bg-gray-800 dark:hover:bg-gray-700 lg:p-3"
                  >
                    <div className="flex flex-col gap-4">
                      <div>
                        <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 text-primary-blue dark:bg-slate-700 lg:mb-2 lg:h-10 lg:w-10">
                          <Package className="h-6 w-6 lg:h-5 lg:w-5" />
                        </div>
                        <p className="line-clamp-2 text-base font-semibold text-gray-900 dark:text-white lg:text-sm">
                          {product.productName}
                        </p>
                        <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                          {product.productCode}
                        </p>
                      </div>

                      <div className="space-y-2 border-t border-slate-100 pt-3 dark:border-slate-700 lg:space-y-1.5 lg:pt-2">
                        <div className="flex items-center justify-between text-sm lg:text-xs">
                          <span className="text-gray-500 dark:text-gray-400">Unidades</span>
                          <span className="font-bold text-gray-900 dark:text-white">
                            {product.totalUnits.toFixed(2)} uds
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-sm lg:text-xs">
                          <span className="text-gray-500 dark:text-gray-400">Pedidos</span>
                          <span className="font-semibold text-gray-900 dark:text-white">
                            {product.totalOrders}
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-sm lg:text-xs">
                          <span className="text-gray-500 dark:text-gray-400">Ventas</span>
                          <span className="font-semibold text-primary-blue">
                            {product.totalSales.toFixed(2)}€
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="rounded-xl bg-white py-8 text-center text-gray-500 shadow-md dark:bg-gray-800 dark:text-gray-400 sm:col-span-2 xl:col-span-3">
                No hay datos de productos disponibles
              </p>
            )}
          </div>
        </section>

        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h3 className={styles.sectionTitle}>Historial de Pedidos</h3>
          </div>
        </section>

        <section className={styles.orderList}>
          {loadingPedidos ? (
            <LoadingSpinner />
          ) : pedidosData?.data && pedidosData.data.length > 0 ? (
            pedidosData.data.map((pedido) => {
              const estadoConfig = statusMap[pedido.estado] || { label: 'Pendiente', style: 'yellow' };
              const badgeClass = `${styles.orderBadge} ${statusBadgeClasses[estadoConfig.style]}`;
              return (
                <div
                  key={pedido.id}
                  className={styles.orderCard}
                  onClick={() => navigate(`/pedidos/${pedido.id}`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      navigate(`/pedidos/${pedido.id}`);
                    }
                  }}
                >
                  <div className={styles.orderHeader}>
                    <div>
                      <span className={styles.mutedText} style={{ fontSize: '0.65rem', letterSpacing: '0.08em' }}>
                        Pedido {pedido.numero}
                      </span>
                      <p className={styles.orderDate}>
                        {new Date(pedido.fecha).toLocaleDateString('es-ES', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </p>
                    </div>
                    <div className={badgeClass}>{estadoConfig.label}</div>
                  </div>
                  <div className={styles.orderFooter}>
                    <div className={styles.orderIcons}>
                      <span className="material-symbols-outlined">inventory_2</span>
                      <span className="material-symbols-outlined">inventory_2</span>
                      <span className="material-symbols-outlined">inventory_2</span>
                    </div>
                    <p className={styles.statsValue}>{pedido.total.toFixed(2)}€</p>
                  </div>
                </div>
              );
            })
          ) : (
            <div className={styles.emptyState}>
              <span className="material-symbols-outlined" style={{ fontSize: '42px', color: '#94a3b8' }}>
                receipt_long
              </span>
              <p className={styles.mutedText}>No hay pedidos registrados</p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
